import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  IonApp,
  IonContent,
  IonFooter,
  IonMenu,
  IonRouterOutlet,
  IonSplitPane,
  ToastController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ScreensizeService } from '@okcargo-apps/layout-ionic';
import { AccountService } from '@okcargo-apps/shared-application-ionic';
import { StorageRepository } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { JwtService, PwaUpdateService } from '@okcargo-apps/shared-infrastructure';
import { PlatformService } from '@okcargo-apps/shared-infrastructure-ionic';
import { CheckVersionService } from '@okcargo-apps/utils-ionic';
import { forkJoin, Observable } from 'rxjs';
import { RefreshDataService } from './common/application/refresh-data.service';
import { MenuFooterComponent } from './layout/components/menu-footer/menu-footer.component';
import { MenuHeaderComponent } from './layout/components/menu-header/menu-header.component';
import { MenuMainComponent } from './layout/components/menu-main/menu-main.component';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [
    CommonModule,
    IonApp,
    IonRouterOutlet,
    IonFooter,
    IonMenu,
    IonSplitPane,
    IonContent,
    TranslateModule,
    MenuHeaderComponent,
    MenuMainComponent,
    MenuFooterComponent,
  ],
  providers: [ScreensizeService],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  private readonly storageService = inject(StorageRepository);
  private readonly jwtService = inject(JwtService);
  private readonly checkVersionService = inject(CheckVersionService);
  private readonly pwaUpdateService = inject(PwaUpdateService);
  private readonly accountService = inject(AccountService);
  private readonly platformService = inject(PlatformService);
  private readonly toastCtrl = inject(ToastController);
  private readonly screensizeService = inject(ScreensizeService);
  private readonly refreshDataService = inject(RefreshDataService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  private _intervalRefreshFreights?: ReturnType<typeof setInterval>;
  private readonly REFRESH_DATA_INTERVAL_MS = 300_000; // 5min

  public readonly isUpdateRequired = this.checkVersionService.isUpdateRequired;

  public isAuthenticateUser = signal<boolean>(false);

  public readonly userCapabilities$: Observable<ECapabilities[]> = this.accountService.userCapabilities$;
  public readonly ECapabilities = ECapabilities;

  public menuPosition = computed(() => {
    return this.screensizeService.isDesktopView() ? 'start' : 'end';
  });

  constructor() {
    const browserLang = navigator.language || navigator.languages[0];
    const defaultLang = browserLang.startsWith('es') ? 'es' : 'en';
    this.translate.setFallbackLang(defaultLang);
    this.translate.use(defaultLang);

    this.accountService.account$.subscribe((account) => {
      if (account && this.translate.getFallbackLang() !== account.lang) {
        this.translate.setFallbackLang(account.lang);
        this.translate.use(account.lang);
      }
    });

    effect(() => {
      if (this.isUpdateRequired()) {
        this.router.navigate(['/update-required'], { replaceUrl: true });
      }
    });

    forkJoin({
      accessToken: this.storageService.read('accessToken'),
      refreshToken: this.storageService.read('refreshToken'),
      username: this.storageService.read('username'),
    }).subscribe({
      next: async (result) => {
        if (result.accessToken.value && result.refreshToken.value && result.username.value) {
          this.jwtService.setAccessToken(result.accessToken.value);
          this.jwtService.setRefreshToken(result.refreshToken.value);
          this.jwtService.setUsername(result.username.value);
          /*
          There is no need to ask for account data, accountService.checkUserAuthentication()
          is called in the authGuard  if there is a token in local storage, and it is
          always executed because at least the minimum AUTHENTICATED capability is checked.
          */
        }
      },
      error: (error) => {
        console.error('tokens:', error);
      },
    });
  }

  async ngOnInit(): Promise<void> {
    await this.platformService.ready();

    if (!this.platformService.isHybrid) {
      effect(
        () => {
          if (this.pwaUpdateService.isUpdateReady()) {
            this.handlePwaUpdate();
          }
        },
        { injector: this.injector },
      );
    }

    this.jwtService.userDataReceived$.subscribe((isAuthenticated) => {
      if (this._intervalRefreshFreights) {
        clearInterval(this._intervalRefreshFreights);
      }

      if (isAuthenticated && !this.isAuthenticateUser()) {
        this.refreshDataService.refreshAll();
      } else if (!isAuthenticated && this.isAuthenticateUser()) {
        this.router.navigate(['/login'], { replaceUrl: true }).then(() => {
          this.redirectToLogin();
        });
      }

      this.isAuthenticateUser.set(isAuthenticated);

      this.zone.runOutsideAngular(() => {
        this._intervalRefreshFreights = setInterval(() => {
          if (this.isAuthenticateUser()) {
            this.zone.run(() => {
              this.refreshDataService.refreshAll();
            });
          }
        }, this.REFRESH_DATA_INTERVAL_MS);
      });
    });
  }

  ngOnDestroy(): void {
    if (this._intervalRefreshFreights) {
      clearInterval(this._intervalRefreshFreights);
    }
  }

  public redirectToLogin(): void {
    window.location.replace('/login');
  }

  public reloadApp(): void {
    window.location.href = window.location.origin;
  }

  private async handlePwaUpdate(): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: this.translate.instant('general.update_version_available'),
      color: 'danger',
      position: 'bottom',
      buttons: [
        {
          text: this.translate.instant('general.update'),
          role: 'info',
          handler: () => {
            this.applyUpdate();
          },
        },
      ],
    });
    await toast.present();
  }

  private async applyUpdate(): Promise<void> {
    await this.pwaUpdateService.activateUpdate();
    this.reloadApp();
  }
}
